import { type FC, useCallback, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { IconMail } from '@redteclab/icons'

import { Link, LoadingButton } from 'base-ui'

import {
  contactFormHandleContactFormSubmit,
  useContactFormApiPostMutation,
  useInitContactFormState,
} from '../../../../contact-form'
import { FriendlyCaptchaChallenge } from '../../../../friendly-captcha'
import { useUserApiSwrSessionV1Customer } from '../../../../user-data'
import { useDirectFormDialogContext } from '../DirectContactFormDialogContext'
import { DirectContactFormEmailInputField } from '../DirectContactFormEmailInputField'
import { DirectContactFormMessageInputField } from '../DirectContactFormMessageInputField'
import { DirectContactFormSelectInputField } from '../DirectContactFormSelectInputField'
import { DirectContactFormTextInfoSection } from '../DirectContactFormTextInfoSection'

export const DirectContactFormDefault: FC<{
  onFormSubmittedWithSuccess: () => void
}> = ({ onFormSubmittedWithSuccess }) => {
  const intl = useIntl()
  const { product, subject } = useDirectFormDialogContext()

  const { formState } = useInitContactFormState({
    formSubject: subject,
    product,
  })

  formState.use()

  const submitMutation = useContactFormApiPostMutation()

  const { data: userData } = useUserApiSwrSessionV1Customer()
  const isLoggedIn = Boolean(userData?.customerNumber)

  const [isFriendlyCaptchaDone, setIsFriendlyCaptchaDone] =
    useState<boolean>(false)

  const captchaDoneCallback = useCallback(
    (fcSolution: string): void => {
      formState.setValue(fcSolution, formState.model, 'fcSolution')
      setIsFriendlyCaptchaDone(true)
    },
    [formState],
  )

  const emailAddress = intl.formatMessage({
    id: 'contactForm.label.email.invitation.email',
  })

  return (
    <>
      <Link
        className="mb-3 flex items-center text-l font-medium no-underline"
        href={`mailto:${emailAddress}`}
      >
        <IconMail className="mr-2 text-2xl text-dark-brand" />
        <p>{emailAddress}</p>
      </Link>

      {isLoggedIn ? null : (
        <DirectContactFormEmailInputField formState={formState} />
      )}

      <DirectContactFormSelectInputField formState={formState} />

      <DirectContactFormMessageInputField formState={formState} />

      <DirectContactFormTextInfoSection />

      <FriendlyCaptchaChallenge
        className="mb-3.5"
        doneCallback={captchaDoneCallback}
        startMode="auto"
        whenThisPropChangesResetCaptcha={submitMutation.error}
      />

      <LoadingButton
        className="mb-2 w-full"
        data-qa-id="message-form-CallbackForm-message"
        disabled={!isFriendlyCaptchaDone}
        loading={submitMutation.isMutating}
        onClick={(event): void => {
          event.preventDefault()
          void contactFormHandleContactFormSubmit(
            formState,
            submitMutation,
            (successValue) => {
              if (successValue) {
                onFormSubmittedWithSuccess()
              }
            },
          )
        }}
        type="submit"
      >
        <FormattedMessage id="product.consultation.button.submitMessage" />
      </LoadingButton>
    </>
  )
}
